import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import type { PublicDrinkItem } from './api'

/**
 * Les bouteilles qu'on ne trouve pas sur les tables.
 *
 * Un champagne, un cognac : on ne les garde pas en salle « au cas où ». Elles
 * existent parce que quelqu'un les a réservées — d'où la mention en tête de
 * section, sans laquelle un client croit pouvoir en commander une au bar le
 * soir venu et repart déçu.
 *
 * La première version demandait de taper un nombre dans un champ, sans qu'on
 * comprenne ce qui se passait ensuite : ni ce qu'on avait retenu, ni ce que ça
 * engageait. Trois corrections :
 *
 * * **on ajoute, on ne saisit pas.** Un bouton « Ajouter », puis − et + une
 *   fois la bouteille retenue. Taper « 3 » dans un champ vide au milieu d'une
 *   carte ne ressemble à aucun geste d'achat.
 * * **le récapitulatif dit ce qui suit.** Trois étapes annoncées : vous
 *   réservez, nous confirmons, vous réglez. Personne ne doit se demander s'il
 *   vient de payer.
 * * **la photo se voit en entier**, à gauche du nom. Une bouteille est haute
 *   et étroite : la recadrer en bandeau lui coupe le goulot et l'étiquette,
 *   c'est-à-dire tout ce qui permet de la reconnaître.
 */
export function OrderOnlyDrinks({
  drinks,
  locale,
  onOrder,
  busy = false,
}: {
  drinks: PublicDrinkItem[]
  locale: string
  /** Ce que le visiteur a retenu, prêt à partir au devis. */
  onOrder: (selection: { drink_item_id: string; quantity: number }[]) => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  const [chosen, setChosen] = useState<Record<string, number>>({})

  const available = useMemo(
    () => drinks.filter((drink) => drink.order_only && drink.availability !== 'sold_out'),
    [drinks],
  )

  const selection = useMemo(
    () =>
      Object.entries(chosen)
        .filter(([, quantity]) => quantity > 0)
        .map(([drink_item_id, quantity]) => ({ drink_item_id, quantity })),
    [chosen],
  )

  if (available.length === 0) return null

  const currency = available[0]?.currency ?? 'EUR'
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency }, locale)

  const total = selection.reduce((sum, row) => {
    const drink = available.find((entry) => entry.id === row.drink_item_id)
    return sum + (drink ? drink.price_minor * row.quantity : 0)
  }, 0)

  function setQuantity(id: string, quantity: number) {
    setChosen((current) => ({ ...current, [id]: Math.max(0, Math.min(50, quantity)) }))
  }

  return (
    <section className="mt-16">
      <div className="mb-6 max-w-2xl">
        <h2 className="font-display text-heading-lg text-content-primary">
          {t('drinks.onOrder.title')}
        </h2>
        <p className="mt-2 text-content-secondary">{t('drinks.onOrder.notice')}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {available.map((drink) => {
          const quantity = chosen[drink.id] ?? 0
          return (
            <Card
              key={drink.id}
              className={cn(
                'flex gap-4 transition',
                quantity > 0 ? 'border-champagne-400/40' : '',
              )}
            >
              {/* Fond neutre et `object-contain` : la bouteille tient en
                  entier dans son cadre, quelle que soit sa forme. */}
              <div className="flex h-32 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md bg-bg-overlay">
                {drink.image_url ? (
                  <img
                    src={drink.image_url}
                    alt={drink.name}
                    className="max-h-full max-w-full object-contain p-1.5"
                    loading="lazy"
                  />
                ) : (
                  <span aria-hidden className="text-2xl opacity-30">
                    🍾
                  </span>
                )}
              </div>

              <div className="flex min-w-0 flex-1 flex-col">
                <h3 className="font-display text-heading-sm text-content-primary">
                  {drink.name}
                </h3>
                {drink.brand || drink.size_label ? (
                  <p className="mt-0.5 text-caption text-content-muted">
                    {[drink.brand, drink.size_label].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
                <p className="mt-1 tabular-nums text-content-primary">
                  {money(drink.price_minor)}
                </p>

                <div className="mt-auto pt-3">
                  {quantity === 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(drink.id, 1)}
                    >
                      {t('drinks.onOrder.add')}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={t('drinks.onOrder.less')}
                        onClick={() => setQuantity(drink.id, quantity - 1)}
                      >
                        −
                      </Button>
                      <span className="w-8 text-center font-display text-heading-sm tabular-nums text-content-primary">
                        {quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={t('drinks.onOrder.more')}
                        onClick={() => setQuantity(drink.id, quantity + 1)}
                      >
                        +
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {selection.length > 0 ? (
        <div className="mt-6 rounded-lg border border-champagne-400/30 bg-bg-elevated p-5">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('drinks.onOrder.recapTitle')}
          </p>

          <ul className="mt-3 grid gap-1.5">
            {selection.map((row) => {
              const drink = available.find((entry) => entry.id === row.drink_item_id)
              if (!drink) return null
              return (
                <li key={row.drink_item_id} className="flex items-baseline gap-3">
                  <span className="min-w-0 flex-1 truncate text-content-primary">
                    {row.quantity} × {drink.name}
                  </span>
                  <span className="shrink-0 tabular-nums text-content-primary">
                    {money(drink.price_minor * row.quantity)}
                  </span>
                </li>
              )
            })}
          </ul>

          <p className="mt-3 flex items-baseline justify-between border-t border-line-subtle pt-3">
            <span className="text-content-secondary">{t('drinks.onOrder.totalLabel')}</span>
            <span className="font-display text-heading-md tabular-nums text-content-primary">
              {money(total)}
            </span>
          </p>

          {/* Ce qui va se passer, en trois temps. Sans cela, on ne sait pas si
              l'on vient de payer, ni ce qu'on attend de nous. */}
          <ol className="mt-4 grid gap-1.5 text-sm text-content-secondary">
            <li>{t('drinks.onOrder.step1')}</li>
            <li>{t('drinks.onOrder.step2')}</li>
            <li>{t('drinks.onOrder.step3')}</li>
          </ol>

          <Button className="mt-5" disabled={busy} onClick={() => onOrder(selection)}>
            {t('drinks.onOrder.submit')}
          </Button>
        </div>
      ) : null}
    </section>
  )
}
